import React, { useState, useEffect } from 'react';
import {
    Wallet, RefreshCw, Loader2, Building2, Shield, CheckCircle2,
    ExternalLink, Copy, UserCheck, Layers, CreditCard, ArrowUpRight
} from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import {
    fetchPocketDetails,
    fetchStoresWhereStaffAssigned,
    currencyCodeToSymbolMap,
    client,
} from '../apiHelper';
import { useToast } from '../hooks/useToast';

export const SeerbitPocketManager = ({ storeId }) => {
    const { showToast } = useToast();
    const [pocket, setPocket] = useState(null);
    const [loading, setLoading] = useState(false);

    const [stores, setStores] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState('');
    const [resolvedStoreId, setResolvedStoreId] = useState(storeId || null);

    const activeStore = stores.find(s => Number(s.store_id) === Number(resolvedStoreId));
    const activeCurrencyCode = activeStore?.setccurr || 'NGN';
    const activeCurrencySymbol = currencyCodeToSymbolMap[activeCurrencyCode] || '₦';

    // 1. Resolve user store if storeId prop isn't directly supplied
    useEffect(() => {
        const resolveUserStore = async () => {
            if (storeId) {
                setResolvedStoreId(storeId);
                return;
            }
            try {
                const stored = localStorage.getItem('user');
                const parsed = stored ? JSON.parse(stored) : null;
                if (parsed) {
                    const returnedStores = await fetchStoresWhereStaffAssigned(parsed);
                    const fetchedStores = returnedStores.filter(store => store.storetype !== 'WHS');
                    if (fetchedStores && fetchedStores.length > 0) {
                        const mappedStores = fetchedStores.map(store => ({
                            label: store.identifier,
                            value: store.store_id,
                            currency: store.setccurr,
                        }));
                        setStores(mappedStores);
                        setSelectedStoreId(fetchedStores[0].store_id);
                        setResolvedStoreId(fetchedStores[0].store_id);
                    }
                }
            } catch (err) {
                console.error("Failed to resolve user assigned store", err);
                showToast(err.response?.data?.detail || "Failed to resolve store context", "error");
            }
        };
        resolveUserStore();
    }, [storeId]);

    // Helper function to package and reconcile pocket payload with local wallet
    const handleWalletReconciliation = async (currentStoreId, pocketData) => {
        if (!pocketData || !pocketData.pocketId) return;

        const payload = {
            store_id: Number(currentStoreId),
            seerbit_pocket_id: pocketData.pocketId,
            currency: pocketData.availableBalanceCurrency || 'NGN',
            current_balance: Number(pocketData.ledgerBalanceAmount || 0),
            available_balance: Number(pocketData.availableBalanceAmount || 0),
            last_seerbit_balance: Number(pocketData.availableBalanceAmount || 0),
            status: (pocketData.status || 'active').toLowerCase(),
        };


        try {
            // console.log(payload)
            const response = await client.postData('/payroll/reconcile-seerbit-wallet', payload);
            showToast(response.msg, "success");
        } catch (err) {
            const errMsg = err.response?.data?.detail || "Wallet reconciliation error:";
            console.error(errMsg, err);
            showToast(errMsg, "error");
        }
    }

    // 2. Load Pocket Details
    const loadPocketDetails = async (storeIdToFetch = resolvedStoreId) => {
        if (!storeIdToFetch) return;

        setLoading(true);
        try {
            const data = await fetchPocketDetails(storeIdToFetch);
            // console.log(data)
            setPocket(data || null);

            if (data && data.pocketId) {
                await handleWalletReconciliation(storeIdToFetch, data);
            }
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to retrieve Pocket details.", "error");
            setPocket(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (resolvedStoreId) {
            loadPocketDetails(resolvedStoreId);
        }
    }, [resolvedStoreId]);

    const formatCurrency = (amount, currencyStr) => {
        if (amount === null || amount === undefined || amount === '') return 'N/A';
        const num = Number(amount);
        if (isNaN(num)) return 'N/A';
        const symbol = currencyCodeToSymbolMap[currencyStr] || activeCurrencySymbol;

        return `${symbol}${num.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };

    const handleCopy = (text, label) => {
        navigator.clipboard.writeText(text);
        showToast(`${label} copied to clipboard!`, "success");
    };

    return (
        <div className="max-w-6xl mx-auto px-4 py-6">
            <Card className="p-6 border-zinc-200 shadow-sm bg-white">
                {/* Header Section */}
                <div className="border-b border-zinc-100 pb-4 mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                            <Wallet size={20} className="text-blue-600" />Organization Virtual Wallet
                        </h2>
                        <p className="text-xs text-zinc-500">
                            Manage merchant virtual account balances, active settlement accounts, and pocket details.
                        </p>
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <select
                            value={selectedStoreId}
                            onChange={(e) => {
                                const newId = e.target.value;
                                setSelectedStoreId(newId);
                                setResolvedStoreId(newId);
                                loadPocketDetails(newId);
                            }}
                            className="bg-zinc-50 border border-zinc-200 text-zinc-700 text-xs rounded-lg px-2.5 py-2 focus:ring-2 focus:ring-blue-600 focus:outline-none transition-all min-w-[140px]"
                        >
                            {stores.map((s) => (
                                <option key={s.value} value={s.value}>
                                    {s.label}
                                </option>
                            ))}
                        </select>

                        <Button
                            onClick={() => loadPocketDetails(resolvedStoreId)}
                            disabled={loading}
                            className="bg-zinc-50 hover:bg-zinc-100 text-zinc-700 text-xs px-3 py-2 border border-zinc-200 flex items-center gap-1.5 whitespace-nowrap"
                        >
                            <RefreshCw size={14} className={loading ? 'animate-spin text-blue-600' : ''} />
                            Refresh
                        </Button>
                    </div>
                </div>

                {loading ? (
                    <div className="p-12 text-center text-zinc-500 text-xs">
                        <Loader2 className="animate-spin inline mr-2 text-blue-600" size={18} /> Loading SeerBit pocket information...
                    </div>
                ) : !pocket ? (
                    <div className="p-12 border border-dashed border-zinc-200 rounded-xl text-center text-zinc-400 text-xs">
                        No SeerBit pocket account linked to this store.
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Core Wallet Overview Row */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Balances Card */}
                            <div className="p-5 border border-blue-100 bg-gradient-to-br from-blue-50/50 to-indigo-50/30 rounded-xl flex flex-col justify-between">
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-xs font-semibold text-blue-900 uppercase tracking-wide">Available Balance</span>
                                        <span className="text-[10px] font-mono bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">
                                            {pocket.status}
                                        </span>
                                    </div>
                                    <h3 className="text-2xl font-extrabold text-zinc-900 font-mono">
                                        {formatCurrency(pocket.availableBalanceAmount, pocket.availableBalanceCurrency)}
                                    </h3>
                                </div>
                                <div className="mt-4 pt-3 border-t border-blue-100/80 flex justify-between items-center text-xs text-zinc-600">
                                    <span>Ledger Balance:</span>
                                    <span className="font-semibold font-mono text-zinc-800">
                                        {formatCurrency(pocket.ledgerBalanceAmount, pocket.ledgerBalanceCurrency)}
                                    </span>
                                </div>
                            </div>

                            {/* Pocket Quick Info */}
                            <div className="p-5 border border-zinc-200 rounded-xl bg-white space-y-2 text-xs flex flex-col justify-between">
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center pb-2 border-b border-zinc-100">
                                        <span className="text-zinc-500">Pocket ID:</span>
                                        <button
                                            onClick={() => handleCopy(pocket.pocketId, "Pocket ID")}
                                            className="font-mono font-bold text-zinc-800 hover:text-blue-600 flex items-center gap-1"
                                        >
                                            {pocket.pocketId} <Copy size={12} className="text-zinc-400" />
                                        </button>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-zinc-500">Main Account #:</span>
                                        <span className="font-mono font-semibold text-zinc-800">{pocket.accountNumber}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-zinc-500">Tier Level:</span>
                                        <span className="font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded text-[10px] border border-indigo-100">
                                            {pocket.tier}
                                        </span>
                                    </div>
                                </div>
                                <div className="pt-2 border-t border-zinc-100 flex justify-between items-center text-[10px] text-zinc-400 font-mono">
                                    <span>Ref: {pocket.reference}</span>
                                    <span className="capitalize">{pocket.pocketFunction.toLowerCase()} MODE</span>
                                </div>
                            </div>

                            {/* Funding & Identity Verification */}
                            <div className="p-5 border border-zinc-200 rounded-xl bg-white space-y-3 text-xs flex flex-col justify-between">
                                <div className="space-y-2">
                                    <span className="text-zinc-500 block font-medium">Verification Details</span>
                                    <div className="bg-zinc-50 p-2.5 rounded-lg space-y-1.5 font-mono text-[11px] border border-zinc-100">
                                        <div className="flex justify-between">
                                            <span className="text-zinc-400 font-sans">Verifier:</span>
                                            <span className="font-semibold text-zinc-800">{pocket.accountVerifierScheme}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-zinc-400 font-sans">Number:</span>
                                            <span className="font-semibold text-zinc-800">{pocket.accountVerifierNumber}</span>
                                        </div>
                                    </div>
                                </div>
                                {pocket.fundingLink && (
                                    <a
                                        href={pocket.fundingLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 text-xs transition-all shadow-xs"
                                    >
                                        Fund Pocket <ArrowUpRight size={14} />
                                    </a>
                                )}
                            </div>
                        </div>

                        {/* Pocket Accounts and Owner Details Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Settlement Bank Accounts */}
                            <div className="p-4 border border-zinc-200 rounded-xl bg-white space-y-3">
                                <div className="flex items-center justify-between font-semibold text-zinc-800 pb-2 border-b border-zinc-100 text-xs">
                                    <span className="flex items-center gap-1.5">
                                        <Building2 size={15} className="text-blue-600" /> Linked Bank Accounts
                                    </span>
                                    <span className="text-[10px] font-normal text-zinc-400">
                                        {pocket.pocketAccounts?.length || 0} Connected
                                    </span>
                                </div>

                                {pocket.pocketAccounts && pocket.pocketAccounts.length > 0 ? (
                                    <div className="space-y-2">
                                        {pocket.pocketAccounts.map((acc) => (
                                            <div
                                                key={acc.accountId}
                                                className="bg-zinc-50 p-3 rounded-lg border border-zinc-100 flex items-center justify-between font-mono text-xs"
                                            >
                                                <div>
                                                    <p className="font-bold text-zinc-800 font-sans">{acc.bankName}</p>
                                                    <p className="text-zinc-500 text-[11px] mt-0.5">Acc: {acc.accountNumber}</p>
                                                </div>
                                                <button
                                                    onClick={() => handleCopy(acc.accountNumber, "Account Number")}
                                                    className="p-1.5 hover:bg-zinc-200 rounded text-zinc-500 transition-all"
                                                    title="Copy Account Number"
                                                >
                                                    <Copy size={13} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-xs text-zinc-400 italic">No linked settlement bank accounts found.</p>
                                )}
                            </div>

                            {/* Owner Profile */}
                            <div className="p-4 border border-zinc-200 rounded-xl bg-white space-y-3">
                                <div className="flex items-center justify-between font-semibold text-zinc-800 pb-2 border-b border-zinc-100 text-xs">
                                    <span className="flex items-center gap-1.5">
                                        <UserCheck size={15} className="text-emerald-600" /> Pocket Owner Profile
                                    </span>
                                    <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-sans font-medium">
                                        <Shield size={10} /> Verified
                                    </span>
                                </div>

                                {pocket.pocketOwner && (
                                    <div className="bg-zinc-50 p-3 rounded-lg border border-zinc-100 space-y-2 text-xs">
                                        <div className="flex justify-between">
                                            <span className="text-zinc-400">Business Name:</span>
                                            <span className="font-bold text-zinc-800">{pocket.pocketOwner.businessName}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-zinc-400">Owner ID:</span>
                                            <span className="font-mono text-zinc-700">{pocket.pocketOwner.pocketOwnerId}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-zinc-400">Email:</span>
                                            <span className="text-zinc-800">{pocket.pocketOwner.emailAddress}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-zinc-400">Phone:</span>
                                            <span className="text-zinc-800">{pocket.pocketOwner.phoneNumber}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default SeerbitPocketManager;